import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useGeoCascade } from '../hooks/useGeoCascade';
import SearchableSelect from './ui/SearchableSelect';
import { phoneForCountry } from '../utils/phoneHelpers';

const AuthModal = ({ isOpen, onClose, onSuccess, mode = 'login' }) => {
  const { login, register, demoLogin } = useAuth();
  const { t } = useTranslation();
  const [currentMode, setCurrentMode] = useState(mode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasError, setHasError] = useState(false); // Track if there's an active error
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    country: '',
    gender: '',
    password: '',
    city: '',          // free text: the এলাকা under the thana in Bangladesh, the city elsewhere
  });

  // Cascade shared with the profile editor so the two cannot drift apart.
  const geo = useGeoCascade(formData.country);

  const countries = [
    { value: 'Bangladesh', label: t('countries.bangladesh') || 'Bangladesh' },
    { value: 'Pakistan', label: t('countries.pakistan') || 'Pakistan' },
    { value: 'India', label: t('countries.india') || 'India' },
    { value: 'Malaysia', label: t('countries.malaysia') || 'Malaysia' },
    { value: 'Indonesia', label: t('countries.indonesia') || 'Indonesia' },
    { value: 'Saudi Arabia', label: t('countries.saudiArabia') || 'Saudi Arabia' },
    { value: 'UAE', label: t('countries.uae') || 'UAE' },
    { value: 'Turkey', label: t('countries.turkey') || 'Turkey' },
    { value: 'Egypt', label: t('countries.egypt') || 'Egypt' },
    { value: 'Other', label: t('countries.other') || 'Other' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
      // Bangladesh brings +88 with it; another country takes it away again.
      ...(name === 'country' ? { phone: phoneForCountry(formData.phone, value) } : {}),
    });
    setError('');
    setHasError(false); // Clear error state when user starts typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setHasError(false);

    try {
      let result;
      if (currentMode === 'login') {
        console.log('🔄 AuthModal: Starting login process');
        result = await login({
          phone: formData.phone,
          password: formData.password,
        });
        console.log('📥 AuthModal: Login result received:', result);
        
        if (result && result.success) {
          console.log('✅ AuthModal: Login successful, calling onSuccess');
          setError('');
          setHasError(false);
          onSuccess();
          onClose();
          return;
        } else {
          // Handle both result.error and case where result is undefined
          const errorMsg = result?.error || 'Login failed. Please check your credentials.';
          console.log('❌ AuthModal: Login failed, setting error:', errorMsg);
          setError(errorMsg);
          setHasError(true); // Mark that we have an active error
          setLoading(false);
          console.log('🔒 AuthModal: Modal should stay open, NOT calling onSuccess or onClose');
          return;
        }
      } else {
        // For registration, directly register user (OTP bypass enabled)
        console.log('🔄 AuthModal: Starting registration process');
        result = await register({
          name: formData.name,
          phone: formData.phone,
          country: formData.country,
          gender: formData.gender,
          password: formData.password,
          // Bangladesh sends the thana — the server derives its district and
          // division — plus an optional এলাকা. Everywhere else sends the
          // free-text city, because no other country in the list has a thana.
          // Both free texts are short_address; api.js drops it when empty.
          ...(geo.bd ? { thana: geo.thana } : {}),
          short_address: formData.city.trim(),
        });
        console.log('📥 AuthModal: Registration result received:', result);
        
        if (result && result.success) {
          console.log('✅ AuthModal: Registration successful, calling onSuccess');
          setError('');
          setHasError(false);
          // The guard needs to know this was a registration, and whether the
          // number is verified, to raise the verify-now prompt.
          onSuccess({ mode: 'register', user: result.user });
          onClose();
          return;
        } else {
          // Handle both result.error and case where result is undefined
          const errorMsg = result?.error || 'Registration failed. Please try again.';
          console.log('❌ AuthModal: Registration failed, setting error:', errorMsg);
          setError(errorMsg);
          setHasError(true); // Mark that we have an active error
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      console.error('🚨 AuthModal: Form submission error caught:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setHasError(true); // Mark that we have an active error
      setLoading(false);
    }
  };


  const handleDemo = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await demoLogin();
      if (result && result.success) {
        setError('');
        setHasError(false);
        onSuccess();
        onClose();
        return;
      } else {
        const errorMsg = result?.error || 'Demo login failed. Please try again.';
        setError(errorMsg);
        setLoading(false);
      }
    } catch (err) {
      console.error('Demo login error:', err);
      setError(err.message || 'Demo login failed. Please try again.');
      setLoading(false);
    }
  };

  // Handle modal close with error protection
  const handleClose = () => {
    console.log('🚪 AuthModal: Close requested, hasError:', hasError, 'error:', error);
    if (hasError && error) {
      console.log('⚠️ AuthModal: Preventing close due to active error');
      return; // Don't close if there's an active error
    }
    console.log('✅ AuthModal: Closing modal');
    setError('');
    setHasError(false);
    onClose();
  };

  if (!isOpen) return null;


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {currentMode === 'login' ? 'Sign In' : 'Create Account'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {currentMode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter your full name"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number (ফোন নম্বর)
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter your phone number (e.g. 01712345678)"
            />
          </div>

          {currentMode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select your country</option>
                {countries.map((country) => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Where the member lives. Bangladesh is the only country this app
              carries administrative data for, so it is the only one that can be
              picked properly; everywhere else gives a free-text area. Collected
              at signup because the community feature matches people by area, and
              asking later means almost nobody fills it in. */}
          {currentMode === 'register' && formData.country && (
            geo.bd ? (
              <>
                <SearchableSelect
                  id="signup-division"
                  label={`${t('address.division')} *`}
                  placeholder={t('address.selectDivision')}
                  options={geo.divisions}
                  value={geo.division}
                  onChange={geo.chooseDivision}
                  triggerClassName="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <SearchableSelect
                  id="signup-district"
                  label={`${t('address.district')} *`}
                  placeholder={t('address.selectDistrict')}
                  options={geo.districts}
                  value={geo.district}
                  onChange={geo.chooseDistrict}
                  disabled={!geo.division}
                  triggerClassName="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <SearchableSelect
                  id="signup-thana"
                  label={`${t('address.thana')} *`}
                  placeholder={t('address.selectThana')}
                  options={geo.thanas}
                  value={geo.thana}
                  onChange={geo.setThana}
                  disabled={!geo.district}
                  triggerClassName="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('address.area')}
                  </label>
                  <input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    maxLength={255}
                    placeholder={t('address.areaPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                {geo.loadError && (
                  <p className="text-xs text-red-600">{t('address.loadFailed')}</p>
                )}
              </>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('address.city')} *
                </label>
                <input
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  placeholder={t('address.cityPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )
          )}

          {currentMode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gender (লিঙ্গ) *
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select your gender</option>
                <option value="M">Male (পুরুষ)</option>
                {/* Shown but not selectable: the service is for men for now. */}
                <option value="F" disabled>Female (মহিলা)</option>
              </select>
              <p className="text-xs text-gray-600 mt-1">
                Required for Islamic same-gender wake-up calls
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 transition duration-200"
          >
            {loading ? 'Please wait...' : currentMode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <div className="mt-4 flex flex-col gap-2">
          <button
            onClick={handleDemo}
            disabled={loading}
            className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 transition duration-200"
          >
            Demo Login (demo123 / demo123)
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setCurrentMode(currentMode === 'login' ? 'register' : 'login')}
              className="text-indigo-600 hover:text-indigo-700 text-sm"
            >
              {currentMode === 'login'
                ? "Don't have an account? Sign up"
                : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;